'use client';

import type { ReactNode } from 'react';

interface ToolbarSegmentedControlProps<T extends string> {
  items: Array<{ key: T; label: string; title?: string; icon?: ReactNode }>;
  value: T | null;
  onChange: (key: T) => void;
  density?: 'comfortable' | 'compact';
  disabled?: boolean;
}

export function ToolbarSegmentedControl<T extends string>({
  items,
  value,
  onChange,
  density = 'comfortable',
  disabled = false,
}: ToolbarSegmentedControlProps<T>) {
  const pad =
    density === 'compact'
      ? 'rounded-full px-3 py-1.5'
      : 'rounded-full px-4 py-1.5';

  return (
    <div
      className={[
        'flex items-center gap-0.5 rounded-full border border-slate-400 bg-blue-50 p-0.5',
        disabled ? 'opacity-40' : '',
      ].join(' ')}
    >
      {items.map(({ key, label, title, icon }) => {
        const tooltip = title ?? (icon ? label : undefined);
        return (
          <div key={key} className="group/tip relative">
            <button
              type="button"
              onClick={() => onChange(key)}
              disabled={disabled}
              aria-pressed={value === key}
              aria-label={icon ? label : undefined}
              className={[
                pad,
                'flex items-center justify-center text-xs font-medium transition-all disabled:cursor-not-allowed',
                value === key
                  ? 'bg-blue-700 text-white shadow-sm'
                  : 'bg-blue-50 text-blue-950 hover:bg-blue-100 hover:text-blue-950',
              ].join(' ')}
            >
              <span className="pointer-events-none">{icon ?? label}</span>
            </button>
            {tooltip && (
              <div className="pointer-events-none absolute top-full left-0 z-50 mt-1.5 w-max max-w-[270px] rounded-md bg-[#171717] px-3 py-1.5 text-xs font-normal text-white opacity-0 shadow-md transition-opacity group-hover/tip:opacity-100">
                <div className="absolute bottom-full left-4 border-4 border-transparent border-b-[#171717]" />
                {tooltip}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
